import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import {
  useReminders,
  useRemindersStats,
  useSendReminderNow,
  usePostponeReminder,
  useCancelReminder,
} from '@/hooks/useReminders'
import { formatDate } from '@/lib/utils'
import {
  ReminderType,
  ReminderPriority,
  ReminderStatus,
  reminderTypeLabels,
  reminderPriorityLabels,
  reminderStatusLabels,
  reminderPriorityColors,
  reminderStatusColors,
  reminderTypeColors,
} from '@/types/reminder'
import type { Reminder } from '@/types/reminder'
import {
  Search,
  Bell,
  Clock,
  AlertTriangle,
  Send,
  Calendar,
  XCircle,
  MoreHorizontal,
  RefreshCw,
  Filter,
  ChevronDown,
  CheckCircle,
  Mail,
  FileText,
  Receipt,
} from 'lucide-react'

// STORY-032: Page de gestion des relances admin

const typeFilters: { value: ReminderType | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous les types' },
  { value: 'devis_pending', label: 'Devis en attente' },
  { value: 'invoice_unpaid', label: 'Facture non payee' },
  { value: 'invoice_overdue', label: 'Facture en retard' },
  { value: 'signature_pending', label: 'Signature en attente' },
  { value: 'question_pending', label: 'Question en attente' },
]

const priorityFilters: { value: ReminderPriority | 'all'; label: string }[] = [
  { value: 'all', label: 'Toutes priorites' },
  { value: 'urgent', label: 'Urgente' },
  { value: 'high', label: 'Haute' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'low', label: 'Basse' },
]

const statusFilters: { value: ReminderStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'scheduled', label: 'Programmee' },
  { value: 'sent', label: 'Envoyee' },
  { value: 'cancelled', label: 'Annulee' },
  { value: 'completed', label: 'Terminee' },
]

export default function RemindersPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<ReminderType | 'all'>('all')
  const [priorityFilter, setPriorityFilter] = useState<ReminderPriority | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<ReminderStatus | 'all'>('pending')
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null)
  const [showPostponeModal, setShowPostponeModal] = useState(false)
  const [reminderToPostpone, setReminderToPostpone] = useState<Reminder | null>(null)
  const [postponeDate, setPostponeDate] = useState('')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [reminderToCancel, setReminderToCancel] = useState<Reminder | null>(null)

  const filters = useMemo(() => ({
    search: search || undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [search, typeFilter, priorityFilter, statusFilter])

  const {
    reminders,
    total,
    isLoading,
    refetch,
  } = useReminders(filters)

  const { data: stats, isLoading: statsLoading } = useRemindersStats()

  const sendNow = useSendReminderNow()
  const postpone = usePostponeReminder()
  const cancel = useCancelReminder()

  const handleSendNow = async (reminder: Reminder) => {
    try {
      await sendNow.mutateAsync(reminder.id)
      setShowActionsMenu(null)
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error)
    }
  }

  const openPostponeModal = (reminder: Reminder) => {
    setReminderToPostpone(reminder)
    // Date par defaut: demain
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    setPostponeDate(tomorrow.toISOString().split('T')[0])
    setShowPostponeModal(true)
    setShowActionsMenu(null)
  }

  const handlePostpone = async () => {
    if (!reminderToPostpone || !postponeDate) return
    try {
      await postpone.mutateAsync({ id: reminderToPostpone.id, newDate: postponeDate })
      setShowPostponeModal(false)
      setReminderToPostpone(null)
      setPostponeDate('')
    } catch (error) {
      console.error('Erreur lors du report:', error)
    }
  }

  const openCancelModal = (reminder: Reminder) => {
    setReminderToCancel(reminder)
    setShowCancelModal(true)
    setShowActionsMenu(null)
  }

  const handleCancel = async () => {
    if (!reminderToCancel) return
    try {
      await cancel.mutateAsync(reminderToCancel.id)
      setShowCancelModal(false)
      setReminderToCancel(null)
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error)
    }
  }

  const getTypeIcon = (type: ReminderType) => {
    switch (type) {
      case 'devis_pending':
      case 'signature_pending':
        return <FileText className="w-4 h-4" />
      case 'invoice_unpaid':
      case 'invoice_overdue':
        return <Receipt className="w-4 h-4" />
      case 'question_pending':
        return <Mail className="w-4 h-4" />
      default:
        return <Bell className="w-4 h-4" />
    }
  }

  const isOverdue = (scheduledAt: string) => {
    return new Date(scheduledAt) < new Date()
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Relances
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez vos relances clients
          </p>
        </div>
        <Button
          variant="outline"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={() => refetch()}
          isLoading={isLoading}
        >
          Actualiser
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-beige-100 flex items-center justify-center">
            <Bell className="w-6 h-6 text-beige-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Total relances</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.total || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gold-100 flex items-center justify-center">
            <Clock className="w-6 h-6 text-gold-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">En attente</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.pending || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
            <Send className="w-6 h-6 text-mint-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Envoyees</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.sent || 0}
            </p>
          </div>
        </Card>

        <Card padding="md" className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-sm text-brown-500">Urgentes</p>
            <p className="text-2xl font-bold text-brown-900">
              {statsLoading ? '-' : stats?.urgentes || 0}
            </p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Rechercher par sujet ou contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ReminderType | 'all')}
              className="w-44"
            >
              {typeFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>

            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as ReminderPriority | 'all')}
              className="w-40"
            >
              {priorityFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ReminderStatus | 'all')}
              className="w-36"
            >
              {statusFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* Reminders list */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-12 text-brown-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-beige-300" />
            <p className="font-medium">Aucune relance trouvee</p>
            <p className="text-sm mt-1">
              {search || typeFilter !== 'all' || priorityFilter !== 'all' || statusFilter !== 'all'
                ? 'Essayez avec d\'autres criteres de recherche'
                : 'Aucune relance programmee pour le moment'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-beige-50 border-b border-beige-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Type / Sujet
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Date prevue
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Priorite
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-brown-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige-200">
                {reminders.map((reminder) => (
                  <tr key={reminder.id} className={`hover:bg-beige-50 ${isOverdue(reminder.scheduled_at) && reminder.status === 'pending' ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          reminderTypeColors[reminder.type].variant === 'gold' ? 'bg-gold-100 text-gold-600' :
                          reminderTypeColors[reminder.type].variant === 'red' ? 'bg-red-100 text-red-600' :
                          reminderTypeColors[reminder.type].variant === 'brown' ? 'bg-brown-100 text-brown-600' :
                          reminderTypeColors[reminder.type].variant === 'blue' ? 'bg-blue-100 text-blue-600' :
                          'bg-beige-100 text-beige-600'
                        }`}>
                          {getTypeIcon(reminder.type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={reminderTypeColors[reminder.type].variant}
                              size="sm"
                            >
                              {reminderTypeLabels[reminder.type]}
                            </Badge>
                            {reminder.escalation_level > 0 && (
                              <span className="text-xs text-brown-500">
                                (Niveau {reminder.escalation_level + 1})
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-brown-900 mt-1 max-w-xs truncate">
                            {reminder.subject}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-brown-900">{reminder.contact_name}</div>
                      <div className="text-sm text-brown-500">{reminder.contact_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${isOverdue(reminder.scheduled_at) && reminder.status === 'pending' ? 'text-red-600 font-medium' : 'text-brown-600'}`}>
                        {formatDate(reminder.scheduled_at)}
                      </div>
                      {isOverdue(reminder.scheduled_at) && reminder.status === 'pending' && (
                        <div className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
                          <AlertTriangle className="w-3 h-3" />
                          En retard
                        </div>
                      )}
                      {reminder.sent_at && (
                        <div className="text-xs text-mint-600 mt-0.5">
                          Envoyee le {formatDate(reminder.sent_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={reminderPriorityColors[reminder.priority].variant}>
                        {reminderPriorityLabels[reminder.priority]}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={reminderStatusColors[reminder.status].variant}>
                        {reminderStatusLabels[reminder.status]}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {(reminder.status === 'pending' || reminder.status === 'scheduled') && (
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionsMenu(showActionsMenu === reminder.id ? null : reminder.id)}
                            className="text-brown-400 hover:text-brown-600 p-1 rounded-lg hover:bg-beige-100"
                          >
                            <MoreHorizontal className="w-5 h-5" />
                          </button>

                          {showActionsMenu === reminder.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowActionsMenu(null)}
                              />
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-beige-200 z-20 py-1">
                                <button
                                  onClick={() => handleSendNow(reminder)}
                                  disabled={sendNow.isPending}
                                  className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <Send className="w-4 h-4" />
                                  Envoyer maintenant
                                </button>
                                <button
                                  onClick={() => openPostponeModal(reminder)}
                                  className="w-full px-4 py-2 text-left text-sm text-brown-700 hover:bg-beige-50 flex items-center gap-2"
                                >
                                  <Calendar className="w-4 h-4" />
                                  Reporter
                                </button>
                                <button
                                  onClick={() => openCancelModal(reminder)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Annuler
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {reminder.status === 'sent' && (
                        <CheckCircle className="w-5 h-5 text-mint-500 inline-block" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination info */}
        {total > 0 && (
          <div className="px-6 py-3 border-t border-beige-200 text-sm text-brown-500">
            {total} relance{total > 1 ? 's' : ''} au total
          </div>
        )}
      </Card>

      {/* Postpone Modal */}
      <Modal
        isOpen={showPostponeModal}
        onClose={() => {
          setShowPostponeModal(false)
          setReminderToPostpone(null)
          setPostponeDate('')
        }}
        title="Reporter la relance"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Reporter la relance{' '}
            <span className="font-medium text-brown-900">
              "{reminderToPostpone?.subject}"
            </span>
          </p>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Nouvelle date
            </label>
            <Input
              type="date"
              value={postponeDate}
              onChange={(e) => setPostponeDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowPostponeModal(false)
                setReminderToPostpone(null)
                setPostponeDate('')
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handlePostpone}
              isLoading={postpone.isPending}
              disabled={!postponeDate}
            >
              Reporter
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false)
          setReminderToCancel(null)
        }}
        title="Annuler la relance"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir annuler la relance{' '}
            <span className="font-medium text-brown-900">
              "{reminderToCancel?.subject}"
            </span>
            ? Cette action est irreversible.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelModal(false)
                setReminderToCancel(null)
              }}
            >
              Non, garder
            </Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              isLoading={cancel.isPending}
            >
              Oui, annuler
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
